import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_3 = { class: "flex flex-col gap-1" }
const _hoisted_4 = { class: "flex flex-col gap-1 mt-2" }
const _hoisted_5 = { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1 flex items-center gap-1" }
const _hoisted_6 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_7 = { class: "grid grid-cols-1 md:grid-cols-3 gap-4" }
const _hoisted_8 = { class: "flex flex-col gap-1 md:col-span-1" }
const _hoisted_9 = { class: "flex flex-col gap-1" }
const _hoisted_10 = { class: "flex flex-col gap-1" }
const _hoisted_11 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_12 = {
  key: 0,
  class: "text-center text-muted-foreground py-8 border-2 border-dashed rounded-md"
}
const _hoisted_13 = { class: "flex flex-col gap-3" }
const _hoisted_14 = { class: "flex items-center gap-2 w-32 shrink-0" }
const _hoisted_15 = ["onUpdate:modelValue"]
const _hoisted_16 = { class: "flex flex-col gap-1 flex-1 w-full" }
const _hoisted_17 = ["onUpdate:modelValue"]
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { LineChart, Plus, Trash2, RefreshCw, BarChart2, Hash } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

interface XYSeries {
  type: 'bar' | 'line'
  dataText: string
}

// Estados visuales

export default /*@__PURE__*/_defineComponent({
  __name: 'XYChartEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

const chartTitle = ref('Ingresos Trimestrales')
const xAxisText = ref('T1, T2, T3, T4')
const yAxisTitle = ref('Ingresos en miles ($)')
const yAxisMin = ref<number | null>(0)
const yAxisMax = ref<number | null>(100)
const series = ref<XYSeries[]>([
  { type: 'bar', dataText: '25, 40, 65, 80' },
  { type: 'line', dataText: '30, 45, 60, 90' }
])

// Acciones
const addSeries = (type: 'bar' | 'line') => {
  series.value.push({ type, dataText: '10, 20, 30, 40' })
}

const removeSeries = (idx: number) => {
  series.value.splice(idx, 1)
}

// Generar Mermaid
const generateMermaid = () => {
  let code = `xychart-beta\n`
  if (chartTitle.value.trim()) {
    code += `    title "${chartTitle.value.trim().replace(/"/g, '')}"\n`
  }
  
  const xArr = xAxisText.value.split(',').map(s => s.trim()).filter(s => s)
  if (xArr.length > 0) {
    code += `    x-axis [${xArr.join(', ')}]\n`
  } else {
    code += `    x-axis [X]\n`
  }
  
  const safeYTitle = yAxisTitle.value.trim().replace(/"/g, '')
  let yAxisStr = `    y-axis`
  
  if (safeYTitle) yAxisStr += ` "${safeYTitle}"`
  if (yAxisMin.value !== null && yAxisMin.value !== undefined && 
      yAxisMax.value !== null && yAxisMax.value !== undefined) {
    yAxisStr += ` ${yAxisMin.value} --> ${yAxisMax.value}`
  }
  
  if (yAxisStr !== `    y-axis`) {
    code += `${yAxisStr}\n`
  }

  series.value.forEach(s => {
    // Validar que los datos sean números válidos
    const dataArr = s.dataText.split(',').map(n => {
      const parsed = parseFloat(n.trim())
      return isNaN(parsed) ? 0 : parsed
    })
    
    if (dataArr.length > 0) {
      code += `    ${s.type} [${dataArr.join(', ')}]\n`
    }
  })
  
  editableCode.value = code
}

// Sincronizar desde Código a Visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newSeries: XYSeries[] = []
  let newTitle = ''
  let newX = ''
  let newYTitle = ''
  let newYMin: number | null = null
  let newYMax: number | null = null
  
  for (const line of lines) {
    const t = line.trim()
    if (t.startsWith('title ')) {
      newTitle = t.replace('title ', '').replace(/"/g, '').trim()
    } else if (t.startsWith('x-axis ')) {
      const match = t.match(/\[(.*?)\]/)
      if (match) newX = match[1]
    } else if (t.startsWith('y-axis ')) {
      const titleMatch = t.match(/"([^"]+)"/)
      if (titleMatch) newYTitle = titleMatch[1]
      
      const rangeMatch = t.match(/([\d.-]+)\s*-->\s*([\d.-]+)/)
      if (rangeMatch) {
        newYMin = parseFloat(rangeMatch[1])
        newYMax = parseFloat(rangeMatch[2])
      }
    } else if (t.startsWith('bar ') || t.startsWith('line ')) {
      const type = t.startsWith('bar') ? 'bar' : 'line'
      const match = t.match(/\[(.*?)\]/)
      if (match) {
        newSeries.push({ type, dataText: match[1] })
      }
    }
  }
  
  chartTitle.value = newTitle
  xAxisText.value = newX
  yAxisTitle.value = newYTitle
  yAxisMin.value = newYMin
  yAxisMax.value = newYMax
  if (newSeries.length > 0) series.value = newSeries
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([chartTitle, xAxisText, yAxisTitle, yAxisMin, yAxisMax, series], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Gráfico XY (Barras y Líneas)",
    description: "Crea gráficos de series temporales o categorías combinando barras y líneas.",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[9] || (_cache[9] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(LineChart), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "default",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (addSeries('bar')))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(BarChart2), { class: "w-4 h-4 mr-2" }),
          _cache[10] || (_cache[10] = _createTextVNode(" Añadir Barras", -1))
        ]),
        _: 1
      }),
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "secondary",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (addSeries('line')))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(LineChart), { class: "w-4 h-4 mr-2" }),
          _cache[11] || (_cache[11] = _createTextVNode(" Añadir Línea", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[15] || (_cache[15] = _createElementVNode("h3", { class: "font-semibold text-lg border-b pb-2" }, "Configuración General", -1)),
          _createElementVNode("div", _hoisted_3, [
            _cache[12] || (_cache[12] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Título del Gráfico", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((chartTitle).value = $event)),
              class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary font-medium",
              placeholder: "Ej. Ingresos Mensuales"
            }, null, 512), [
              [_vModelText, chartTitle.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("label", _hoisted_5, [
              _createVNode(_unref(Hash), { class: "w-3 h-3" }),
              _cache[13] || (_cache[13] = _createTextVNode(" Eje X (Categorías separadas por coma) ", -1))
            ]),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((xAxisText).value = $event)),
              class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary",
              placeholder: "Ej. Ene, Feb, Mar, Abr"
            }, null, 512), [
              [_vModelText, xAxisText.value]
            ]),
            _cache[14] || (_cache[14] = _createElementVNode("p", { class: "text-[10px] text-muted-foreground mt-1 ml-1" }, "Nota: Asegúrate de tener la misma cantidad de datos en cada serie que las categorías del Eje X.", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[19] || (_cache[19] = _createElementVNode("h3", { class: "font-semibold text-lg border-b pb-2" }, "Configuración Eje Y", -1)),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[16] || (_cache[16] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Título Eje Y", -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((yAxisTitle).value = $event)),
                class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary",
                placeholder: "Ej. Cantidad"
              }, null, 512), [
                [_vModelText, yAxisTitle.value]
              ])
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[17] || (_cache[17] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Valor Mínimo (Opcional)", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "number",
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((yAxisMin).value = $event)),
                class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary",
                placeholder: "Auto"
              }, null, 512), [
                [
                  _vModelText,
                  yAxisMin.value,
                  void 0,
                  { number: true }
                ]
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _cache[18] || (_cache[18] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Valor Máximo (Opcional)", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "number",
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((yAxisMax).value = $event)),
                class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary",
                placeholder: "Auto"
              }, null, 512), [
                [
                  _vModelText,
                  yAxisMax.value,
                  void 0,
                  { number: true }
                ]
              ])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _cache[22] || (_cache[22] = _createElementVNode("h3", { class: "font-semibold text-lg border-b pb-2" }, "Series de Datos", -1)),
          (series.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, " No hay series de datos. Añade Barras o Líneas. "))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_13, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(series.value, (s, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: idx,
                class: "flex items-start md:items-center gap-3 bg-muted/40 border p-3 rounded-md relative flex-col md:flex-row"
              }, [
                _createElementVNode("div", _hoisted_14, [
                  (s.type === 'bar')
                    ? (_openBlock(), _createBlock(_unref(BarChart2), {
                        key: 0,
                        class: "w-5 h-5 text-blue-500"
                      }))
                    : _createCommentVNode("", true),
                  (s.type === 'line')
                    ? (_openBlock(), _createBlock(_unref(LineChart), {
                        key: 1,
                        class: "w-5 h-5 text-emerald-500"
                      }))
                    : _createCommentVNode("", true),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": ($event: any) => ((s.type) = $event),
                    class: "bg-transparent border-b font-bold text-sm focus:outline-none capitalize focus:border-primary"
                  }, [...(_cache[20] || (_cache[20] = [
                    _createElementVNode("option", { value: "bar" }, "Barra", -1),
                    _createElementVNode("option", { value: "line" }, "Línea", -1)
                  ]))], 8, _hoisted_15), [
                    [_vModelSelect, s.type]
                  ])
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _cache[21] || (_cache[21] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Datos (Separados por coma)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((s.dataText) = $event),
                    class: "bg-background border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono w-full",
                    placeholder: "Ej. 10, 20, 30, 40"
                  }, null, 8, _hoisted_17), [
                    [_vModelText, s.dataText]
                  ])
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeSeries(idx)),
                  class: "md:ml-2 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-2 rounded-full absolute right-2 top-2 md:relative md:top-auto md:right-auto",
                  title: "Eliminar Serie"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_18)
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_19, [
        _cache[24] || (_cache[24] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[23] || (_cache[23] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})